import type { DeclaredClientTool } from '@/shared/http/agents';
import type { ActionKind, ScreenManifest, ScreenRegistry } from '@/shared/screen';
import { recordAgentAction } from '@/shared/telemetry';

import {
  toolFailure,
  toolText,
  type AgentTool,
  type AgentToolInput,
  type AgentToolResult,
} from './agent-tool';
import { isManualOnlyScreen } from './manual-only';
import { RUNNABLE_KINDS } from './screen-actions';

export { isManualOnlyScreen } from './manual-only';

/**
 * As client tools que o Luy declara ao motor, com o nome EXATO da `tool_allowlist` do `uyzy.luy` (G48, D-L07)
 * e a meta-ferramenta do registro que cada uma roda.
 */
export const CLIENT_TOOLS = {
  uyzy_get_screen_guide: 'get_screen_guide',
  uyzy_list_screens: 'list_screens',
  uyzy_open_screen: 'open_screen',
  uyzy_get_screen_actions: 'get_screen_actions',
  uyzy_run_screen_action: 'run_screen_action',
} as const satisfies Readonly<Record<string, string>>;

export type ClientToolName = keyof typeof CLIENT_TOOLS;

/** Abaixo do `ClientToolWait` de 2 min do motor: a falha volta antes de o turno vencer (03 §2). */
export const CLIENT_TOOL_TIMEOUT_MS = 20_000;

/** Teto do `output` devolvido ao motor; o resto é cortado com `OUTPUT_TRUNCATED`. */
export const CLIENT_TOOL_OUTPUT_MAX = 12_000;

export const OUTPUT_TRUNCATED = '…[truncated]';

/** O nome declarado da ferramenta que executa ações da tela; o chat resume o preenchimento a partir dela. */
export const RUN_SCREEN_ACTION_TOOL = 'uyzy_run_screen_action' satisfies ClientToolName;

/** Aviso no guia (U56): o Luy prepara rascunhos; gravar, enviar e aprovar são da pessoa (B7 fica para a fase 2). */
// eslint-disable-next-line lingui/no-unlocalized-strings -- texto para o modelo, inglês canônico (U33)
export const GUIDE_PHASE_NOTE = `Only ${Object.keys(CLIENT_TOOLS).join(', ')} are available to you. On the open screen you can read, filter, navigate and prepare drafts (fill fields) with uyzy_get_screen_actions and uyzy_run_screen_action: the actions marked runnable in this guide. Saving, sending, approving and anything else that changes data are done by the person in the app: point to the button, and never say you saved or sent anything. You never overwrite a field the person already edited.`;

export interface ClientToolCall {
  readonly name: string;
  /** Veio do modelo: não confiável (G26). */
  readonly input: unknown;
}

/** O que vai no `POST …/tool-results`: texto curto e mascarado; recusa é `succeeded: false`. */
export interface ClientToolResult {
  readonly succeeded: boolean;
  readonly output: string;
}

export interface ClientToolBridge {
  /** As `clientTools` do turno, no idioma ativo (U46); só as que estão registradas agora. */
  declare(): DeclaredClientTool[];
  /** Executa a invocação; nunca lança. */
  run(call: ClientToolCall, signal?: AbortSignal): Promise<ClientToolResult>;
}

export interface ClientToolBridgeDeps {
  readonly tools: { tools(): readonly AgentTool[] };
  readonly screens: ScreenRegistry;
  /** Origem na trilha (U24), ex.: `luy`. */
  readonly origin: string;
  /** A tela montada agora: o `uyzy_open_screen` dela responde que já está aberta, mesmo fora do menu (U56). */
  readonly activeScreen?: () => string | null;
  readonly timeoutMs?: number;
  readonly now?: () => number;
  /** Trilha (U24); padrão, a telemetria. Injetável: com `--no-isolate`, o `vi.mock` do módulo não alcança um `client-tools` já carregado. */
  readonly record?: typeof recordAgentAction;
}

const isClientTool = (name: string): name is ClientToolName => Object.hasOwn(CLIENT_TOOLS, name);

const MENTIONS = new RegExp(`\\b(${Object.values(CLIENT_TOOLS).join('|')})\\b`, 'g');
const PREFIXED: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(CLIENT_TOOLS).map(([declared, internal]) => [internal, declared]),
);

const declaredNames = (text: string) =>
  text.replace(MENTIONS, (mention) => PREFIXED[mention] ?? mention);

/** A descrição com a nota do turno (a tela que a pessoa vê agora, U56), lida a cada declaração. */
function describe(tool: AgentTool): string {
  const note = tool.turnNote?.().trim();
  return declaredNames(note ? `${tool.description} ${note}` : tool.description);
}

function declareTool(name: ClientToolName, tool: AgentTool): DeclaredClientTool {
  const { $schema: _schema, ...schema } = tool.inputSchema;
  return {
    name,
    description: describe(tool),
    inputSchema: JSON.stringify(schema),
    // B7: `readOnly: false` sem builder de prévia no motor derruba o turno, e a escrita já confirma aqui
    // (confirmação dupla, 02 §3). Escrita e destrutiva nunca são declaradas na fase 1 (D-L06).
    readOnly: true,
  };
}

function textOf(result: AgentToolResult): string {
  return result.content.map((part) => part.text).join('\n');
}

/** Corta no teto. Sem máscara: o Luy lê e escreve CPF e CNPJ (U56, L07); o WebMCP do navegador mascara no adaptador. */
export function clientToolOutput(text: string): string {
  return text.length > CLIENT_TOOL_OUTPUT_MAX
    ? `${text.slice(0, CLIENT_TOOL_OUTPUT_MAX - OUTPUT_TRUNCATED.length)}${OUTPUT_TRUNCATED}`
    : text;
}

type Settled =
  | { kind: 'done'; result: AgentToolResult }
  | { kind: 'failed' }
  | { kind: 'timeout' }
  | { kind: 'aborted' };

/**
 * Roda com prazo e cancelamento. Ao desistir, aborta o `signal` da execução: a ferramenta que ainda não
 * começou nem começa, e o `open_screen` não navega depois de a resposta sair (R2). Um `nav.to` já chamado é
 * do roteador.
 */
function settle(
  work: (signal: AbortSignal) => Promise<AgentToolResult>,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<Settled> {
  if (signal?.aborted) return Promise.resolve({ kind: 'aborted' });
  const execution = new AbortController();
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: Settled) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      if (value.kind === 'timeout' || value.kind === 'aborted') execution.abort();
      resolve(value);
    };
    const onAbort = () => finish({ kind: 'aborted' });
    const timer = setTimeout(() => finish({ kind: 'timeout' }), timeoutMs);
    signal?.addEventListener('abort', onAbort, { once: true });
    Promise.resolve()
      .then(() => (execution.signal.aborted ? null : work(execution.signal)))
      .then(
        (result) => {
          if (result) finish({ kind: 'done', result });
        },
        () => finish({ kind: 'failed' }),
      );
  });
}

function resultOf(settled: Settled, timeoutMs: number): ClientToolResult {
  switch (settled.kind) {
    case 'done':
      return {
        succeeded: settled.result.isError !== true,
        output: clientToolOutput(textOf(settled.result)),
      };
    case 'failed':
      return { succeeded: false, output: textOf(toolFailure('The tool failed in the browser.')) };
    case 'timeout':
      return {
        succeeded: false,
        output: textOf(toolFailure(`Timed out after ${String(timeoutMs)} ms.`)),
      };
    case 'aborted':
      return { succeeded: false, output: textOf(toolFailure('Cancelled by the user.')) };
  }
}

function inputOf(raw: unknown): AgentToolInput | null {
  if (raw === undefined || raw === null) return {};
  return typeof raw === 'object' && !Array.isArray(raw) ? (raw as AgentToolInput) : null;
}

/** Tela que o `list_screens` mostra: no menu e sem parâmetro de rota (R4). */
function isMenuScreen(screen: ScreenManifest): boolean {
  return screen.nav !== undefined && !screen.path.includes('$');
}

function parsed(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A ação do guia sem a ferramenta da tela, que o motor não conhece (R5): no lugar, `runnable` diz se o Luy a
 * executa agora pelo `uyzy_run_screen_action` (U56).
 */
function withoutToolRefs(action: unknown): unknown {
  if (!isRecord(action)) return action;
  const { tool: _tool, availableNow, ...rest } = action;
  return {
    ...rest,
    runnable: availableNow === true && RUNNABLE_KINDS.has(rest['kind'] as ActionKind),
  };
}

/** A ponte do registro de agente para as `clientTools` do motor (G29): declara e executa (L3). */
export function createClientToolBridge(deps: ClientToolBridgeDeps): ClientToolBridge {
  const timeoutMs = deps.timeoutMs ?? CLIENT_TOOL_TIMEOUT_MS;
  const activeScreen = deps.activeScreen ?? (() => null);
  const now = deps.now ?? (() => performance.now());
  const find = (internal: string) => deps.tools.tools().find((tool) => tool.name === internal);
  const manualOnly = (id: unknown) => {
    const screen = typeof id === 'string' ? deps.screens.byId(id) : undefined;
    return screen !== undefined && isManualOnlyScreen(deps.screens, screen);
  };
  const manualRefusal = (id: string) =>
    toolFailure(
      `Screen ${id} is manual only: login, MFA, step-up and Security are never opened by the assistant.`,
    );

  function refusal(call: ClientToolCall, input: AgentToolInput | null): AgentToolResult | null {
    if (!isClientTool(call.name)) return toolFailure(`Unknown client tool ${call.name}.`);
    if (input === null) return toolFailure('Invalid input: expected an object.');
    const screenId = input['screenId'];
    if (call.name === 'uyzy_list_screens' || typeof screenId !== 'string') return null;
    if (manualOnly(screenId)) return manualRefusal(screenId);
    const screen = deps.screens.byId(screenId);
    if (
      call.name === 'uyzy_open_screen' &&
      screen &&
      !isMenuScreen(screen) &&
      screenId !== activeScreen()
    )
      return toolFailure(
        `Screen ${screenId} is not a menu screen. Open only ids returned by uyzy_list_screens.`,
      );
    return null;
  }

  /** A segunda trava da U24 também no que sai (R3) e o guia sem ferramentas fora do alcance do motor (R5). */
  function shape(name: ClientToolName, result: AgentToolResult): AgentToolResult {
    if (result.isError) return result;
    if (name === 'uyzy_open_screen') return toolText(declaredNames(textOf(result)));
    const value = parsed(textOf(result));
    if (name === 'uyzy_list_screens' && Array.isArray(value))
      return toolText(
        JSON.stringify(value.filter((row) => !(isRecord(row) && manualOnly(row['id'])))),
      );
    if (name !== 'uyzy_get_screen_guide' || !isRecord(value)) return result;
    if (manualOnly(value['screenId'])) return manualRefusal(String(value['screenId']));
    const guide = isRecord(value['guide']) ? value['guide'] : null;
    const declared = new Set<string>(Object.keys(CLIENT_TOOLS));
    return toolText(
      JSON.stringify({
        ...value,
        note: GUIDE_PHASE_NOTE,
        guide: guide && {
          ...guide,
          actions: (Array.isArray(guide['actions']) ? guide['actions'] : []).map(withoutToolRefs),
          related: (Array.isArray(guide['related']) ? guide['related'] : []).filter(
            (related) => !(isRecord(related) && manualOnly(related['id'])),
          ),
        },
        tools: (Array.isArray(value['tools']) ? value['tools'] : []).filter(
          (tool) =>
            isRecord(tool) && typeof tool['name'] === 'string' && declared.has(tool['name']),
        ),
      }),
    );
  }

  async function execute(call: ClientToolCall, signal?: AbortSignal): Promise<Settled> {
    const input = inputOf(call.input);
    const refused = refusal(call, input);
    if (refused || !isClientTool(call.name))
      return { kind: 'done', result: refused ?? toolFailure('Unknown client tool.') };
    const name = call.name;
    const tool = find(CLIENT_TOOLS[name]);
    if (!tool)
      return {
        kind: 'done',
        result: toolFailure(`Client tool ${name} is not available on this screen right now.`),
      };
    const settled = await settle(
      (execution) => tool.execute(input ?? {}, { signal: execution }),
      timeoutMs,
      signal,
    );
    return settled.kind === 'done'
      ? { kind: 'done', result: shape(name, settled.result) }
      : settled;
  }

  return {
    declare: () =>
      (Object.keys(CLIENT_TOOLS) as ClientToolName[]).flatMap((name) => {
        const tool = find(CLIENT_TOOLS[name]);
        return tool ? [declareTool(name, tool)] : [];
      }),
    async run(call, signal) {
      const started = now();
      const settled = await execute(call, signal);
      const latencyMs = Math.max(0, Math.round(now() - started));
      const result = resultOf(settled, timeoutMs);
      const outcome = result.succeeded ? 'ok' : settled.kind === 'done' ? 'denied' : 'error';
      (deps.record ?? recordAgentAction)(
        isClientTool(call.name) ? call.name : 'unknown',
        deps.origin,
        outcome,
        latencyMs,
      );
      return result;
    },
  };
}
